import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { splitEvery, uniq, uniqBy } from 'ramda';
import { useMemo, useState } from 'react';

import { createMediaFromUrl, createProduct } from 'apis/product-information-catalog/api';
import { CreateProductItemResponse, Product } from 'apis/product-information-catalog/types';
import { searchProducts } from 'apis/product-information-service/api';
import { Item, ProductHierarchyLevel } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { getErrorContent, isDefined } from 'common/utils';
import { allItemsInteractionClickAddItemsToCatalog } from 'core/telemetry/avo/Avo';
import { useVenueContext } from 'modules/venue/VenueContext';

import { getQueryKeys, isCreateProductError, transformItemToProductData } from './utils';

const CHUNK_SIZE = 10;

type ItemWithGtin = Omit<Item, 'barcode_gtin'> & {
  barcode_gtin: string;
};

type Props = {
  items: Item[];
  selectedItemIds: string[];
  country: string;
  productHierarchyLevels: ProductHierarchyLevel[];
  onDone: (data: {
    createdProducts: Product[];
    productsByError: Record<string, Product[]>;
    errorMessages: string[];
    failToUploadImagesItem: ItemWithGtin[];
  }) => void;
};

const useAddItemsToCatalog = ({ items, country, selectedItemIds, onDone, productHierarchyLevels }: Props) => {
  const queryClient = useQueryClient();
  const venue = useVenueContext();
  const [progressPercentage, setProgressPercentage] = useState(0);
  const queryKeys = getQueryKeys(venue);
  const itemsWithGtin = useMemo(
    () => items.filter((item): item is ItemWithGtin => item.barcode_gtin !== null && !item.source_config),
    [items],
  );
  const {
    data: itemsNotInCatalog,
    isLoading: isLoadingItemsNotInCatalog,
    error: errorItemsNotInCatalog,
    remove: removeItemsNotInCatalogQuery,
  } = useQuery(
    [queryKeys.searchProductsToAdd, itemsWithGtin],
    async () => {
      const gtins = itemsWithGtin.map(item => item.barcode_gtin);
      return searchProducts(country, gtins);
    },
    {
      select: existingProducts =>
        itemsWithGtin.filter(
          item =>
            !existingProducts.some(product => product.gtin.replace(/^0+/, '') === item.barcode_gtin.replace(/^0+/, '')),
        ),
    },
  );

  const {
    mutate,
    isLoading: addingItems,
    error: uploadError,
  } = useMutation(
    async () => {
      if (!itemsNotInCatalog) {
        throw new Error('Items are not available. This should not happen!');
      }

      const selectedItems = itemsNotInCatalog.filter(item => selectedItemIds.includes(item.id));
      if (selectedItems.length === 0) {
        throw new Error('Unknown selected items. This should not happen!');
      }

      const createdProductResponses: CreateProductItemResponse[] = [];
      const errorResponses: unknown[] = [];
      const someImagesFailedToUpload: ItemWithGtin[] = [];
      const itemChunks = splitEvery(CHUNK_SIZE, selectedItems);

      for (const [index, chunk] of itemChunks.entries()) {
        const percentage = (100 * ((index + 1) * CHUNK_SIZE)) / selectedItems.length;
        setProgressPercentage(Math.min(percentage, 100));

        const payloadPromises = chunk.map(async item => {
          const mediaIds: string[] = [];

          for (const image of item.images) {
            await createMediaFromUrl(image.url)
              .then(media => {
                mediaIds.push(media.id);
              })
              .catch(() => {
                someImagesFailedToUpload.push(item);
                return undefined;
              });
          }

          return transformItemToProductData(item, productHierarchyLevels, mediaIds.filter(isDefined));
        });
        const payload = await Promise.all(payloadPromises);

        await createProduct(country, { items: payload })
          .then(createProductResponse => {
            createdProductResponses.push(...createProductResponse.items);
          })
          .catch(error => {
            errorResponses.push(error);
          });
      }

      const productsByError = createdProductResponses.reduce<Record<string, Product[]>>(
        (record, { status, item: product }) => {
          if (status.details !== undefined) {
            const products = record[status.details] ?? [];
            record[status.details] = [...products, product];
          }
          return record;
        },
        {},
      );

      const errorMessages = uniq(
        errorResponses
          .map(error => (isCreateProductError(error) ? error.response?.data.details : getErrorContent(error)))
          .filter(isDefined),
      );

      const createdProducts = createdProductResponses
        .filter(res => res.status.details === undefined)
        .map(res => res.item);

      return {
        createdProducts,
        productsByError,
        errorMessages,
        failToUploadImagesItem: uniqBy(item => item.barcode_gtin, someImagesFailedToUpload),
      };
    },
    {
      onSuccess: ({ createdProducts, productsByError, errorMessages, failToUploadImagesItem }) => {
        allItemsInteractionClickAddItemsToCatalog({ country, numberOfItems: createdProducts.length });
        queryClient.removeQueries(queryKeys.searchProductsToConnect);
        removeItemsNotInCatalogQuery();
        onDone({ createdProducts, productsByError, errorMessages, failToUploadImagesItem });
      },
    },
  );

  return {
    itemsNotInCatalog,
    initialLoading: isLoadingItemsNotInCatalog,
    addingItems,
    error: errorItemsNotInCatalog || uploadError,
    progressPercentage,
    addItemsToCatalog: mutate,
  };
};

export default useAddItemsToCatalog;
